/**
 * Application Service: Select Analytic Plan
 *
 * Manages the UI state and business logic for the analytic plan selection table/grid.
 * This service handles:
 * - Rendering the hierarchical level structure as a flat table
 * - Filtering and pagination
 * - Row visibility management (checkboxes)
 * - Amount input and validation
 * - Percentage calculations
 * - Parent amount recalculation based on children
 *
 * The table displays a hierarchical tree structure flattened into rows,
 * with rowspan calculations to merge cells for parent nodes.
 *
 * @module analitic_accounting/application/SelectAnalyticPlan
 */

/**
 * Application service for managing analytic plan selection UI.
 * Coordinates between the plan data and the table rendering.
 */
export class SelectAnalyticPlan{
  /** Reactive screen data object (typically Vue reactive) */
  screen_data;

  /** The LineAnalyticPlan being edited */
  plan;

  /** All rows without filter applied (cache for filtering) */
  allRows = [];

  /** Callback function to notify when plan is updated */
  setLineAnalyticPlanUdated;

  /** Whether to show percentage column in the UI */
  show_percent;

  /**
   * Creates the select analytic plan service
   * @param screen_data - Reactive object for UI binding (typically Vue data)
   * @param setLineAnalyticPlanUdated - Callback to invoke when plan changes
   * @param show_percent - Whether to display percentage column (default: false)
   */
  constructor(screen_data, setLineAnalyticPlanUdated, show_percent = false){
    this.screen_data = screen_data
    this.setLineAnalyticPlanUdated = setLineAnalyticPlanUdated
    this.show_percent = show_percent
  }

  /**
   * Executes the service with a plan - initializes and prepares the table view.
   * This is the main entry point after the service is constructed.
   *
   * Steps:
   * 1. Initialize ui_visible properties on all levels
   * 2. Generate rows from the hierarchical structure
   * 3. Calculate rowspans for table rendering
   * 4. Validate amounts
   *
   * @param plan - The LineAnalyticPlan to display/edit
   */
  execute(plan){
    this.plan = plan
    this.initialize_ui_visible()
    this.get_rows()
    this.calcularRowspans()
    this.validate_amounts()
  }

  initialize_ui_visible(){
    // Initialize ui_visible for all levels - only if not already set
    const initializeLevelVisibility = (levels, isLevel2 = true) => {
      if (!levels || levels.length === 0) return;
      levels.forEach((level, idx) => {
        // Only initialize if ui_visible is not already set
        if (level.ui_visible === undefined || level.ui_visible === null) {
          level.ui_visible = false;
        }
        if (level.levels && level.levels.length > 0) {
          initializeLevelVisibility(level.levels, false);
        }
      });
    };

    if (this.plan && this.plan.levels && this.plan.levels.length > 0) {
      this.plan.levels.forEach(parent => {
        // Initialize parent ui_visible if not set
        if (parent.ui_visible === undefined || parent.ui_visible === null) {
          parent.ui_visible = false;
        }
        if (parent.levels && parent.levels.length > 0) {
          initializeLevelVisibility(parent.levels, true);
        }
      });
    }
  }

  validate_amounts(){
    this.plan.validate_level_amounts()
  }

  amount_changed(level, amount) {
    const trimmed = String(amount).trim();
    const num = (trimmed === '' || isNaN(parseFloat(trimmed))) ? null : parseFloat(trimmed);
    level.amount = num;

    // Recalculate the amounts of the parents based on the sum of their children
    this.recalculateParentAmounts();

    this.setLineAnalyticPlanUdated(true);
    this.validate_amounts()
  }

  recalculateParentAmounts() {
    // Function to recursively recalculate the amounts of the parent nodes
    const recalculate = (node) => {
      if (!node.levels || node.levels.length === 0) {
        // It's a leaf node, do nothing
        return node.amount;
      }

      // It has children, recursively recalculate each child first
      node.levels.forEach(child => recalculate(child));

      // Check if all children have null amounts
      const allChildrenNull = node.levels.every(child =>
        child.amount === null || child.amount === undefined
      );

      if (allChildrenNull) {
        // If all children are null, parent should be null too
        node.amount = null;
        return null;
      }

      // Sum the amounts of all children (treating null as 0 for calculation)
      const childrenSum = node.levels.reduce((sum, child) => {
        const childAmount = child.amount !== null && child.amount !== undefined ? parseFloat(child.amount) : 0;
        return sum + childAmount;
      }, 0);

      // Update the father's amount
      node.amount = parseFloat(childrenSum.toFixed(2));

      return node.amount;
    };

    // Recalculate all level 1 nodes
    if (this.plan && this.plan.levels) {
      this.plan.levels.forEach(rootNode => recalculate(rootNode));
    }
  }

  get_rows(){
    const results = []

    // Recursive function to traverse the tree
    // depth: 0 = level 1 (parents), 1 = level 2 (children), 2+ = grandchildren...
    const traverse = (node, path, depth) => {
      const newPath = [...path, node]

      // Show ALL levels regardless of ui_visible to allow maximum flexibility
      // But ONLY show leaf nodes (rows) that have an amount defined
      // This way, when a non-predefined plan is selected with no amounts, table is empty

      if (!node.levels || node.levels.length === 0) {
        // Leaf node - only add if THIS NODE (the leaf) has an amount
        // Don't show it just because a parent has an amount
        if (node.amount !== null && node.amount !== undefined) {
          results.push({ path: newPath })
        }
      } else {
        // Has children - traverse all children
        node.levels.forEach(child => traverse(child, newPath, depth + 1))
      }
    }

    this.plan.levels.forEach(parent => traverse(parent, [], 0))

    this.screen_data.maxDepth = results.length
      ? Math.max(...results.map(r => r.path.length))
      : 0

    // Pad paths to max depth
    const paddedRows = results.map(r => {
      const completePath = [...r.path]
      while (completePath.length < this.screen_data.maxDepth) {
        completePath.push(null)
      }
      return { path: completePath }
    })

    // Store original rows and initialize visible rows
    this.allRows = paddedRows
    this.screen_data.rows = [...this.allRows]
  }

  refreshRows(){
    // Recalculate rows based on current ui_visible state
    this.get_rows()
    this.calcularRowspans()

    // Recalculate parent amounts after visibility changes
    this.recalculateParentAmounts()
    this.validate_amounts()

    console.log('blackraven: Rows refreshed, current visible rows:', this.screen_data.rows);
  }

  calcularRowspans() {
    const nFiles = this.screen_data.rows.length
    const maxDepth = this.screen_data.maxDepth

    this.screen_data.rowspanMap = Array.from({ length: nFiles }, () =>
      Array(maxDepth).fill(0)
    )
    for (let col = 0; col < maxDepth; col++) {
      let prevKey = ''
      let rowspan = 1
      for (let row = 0; row < nFiles; row++) {
        const cell = this.screen_data.rows[row]?.path[col]
        const key = cell ? cell.code : ''
        if (key === prevKey) {
          rowspan++
          this.screen_data.rowspanMap[row][col] = 0
        } else {
          if (row > 0 && (row - rowspan) >= 0 && this.screen_data.rowspanMap[row - rowspan][col] === 0) {
            this.screen_data.rowspanMap[row - rowspan][col] = rowspan
          }
          prevKey = key
          rowspan = 1
        }
      }
      if (nFiles > 0) {
        const lastRow = nFiles - rowspan
        if (this.screen_data.rowspanMap[lastRow][col] === 0) {
          this.screen_data.rowspanMap[lastRow][col] = rowspan
        }
      }
    }
    for (let col = 1; col < maxDepth; col++) {
      for (let row = 0; row < nFiles; row++) {
        //if(this.screen_data.rowspanMap[row][col]==0)
          this.screen_data.rowspanMap[row][col] = 1
      }
    }
    
  }

  shouldShowCell(row, col) {
    return (this.screen_data.rowspanMap[row]?.[col] ?? 0) > 0
  }

  // 🔎 Filtre per nom, codi o import (conté, case-insensitive)
  applyFilter() {
    const t = (this.screen_data.filterText ?? '').toString().trim().toLowerCase()

    if (!t) {
      this.screen_data.rows = [...this.allRows]
      this.calcularRowspans()
      return
    }

    const match = (level) => {
      if (!level) return false
      const nameOk = level.name && level.name.toString().toLowerCase().includes(t)
      const codeOk = level.code && level.code.toString().toLowerCase().includes(t)
      return !!(nameOk || codeOk )
    }

    this.screen_data.rows = this.allRows.filter(r =>
      r.path.some(level => match(level))
    )
    this.calcularRowspans()
  }

  calculatePercent(level, totalAmount) {
    if (!level || level.amount === null || totalAmount === 0) {
      return null
    }
    const percent = (Math.abs(level.amount) / Math.abs(totalAmount)) * 100
    return Math.round(percent * 100) / 100 // Round to 2 decimals
  }

  updatePercentage(level, totalAmount, newPercentValue) {
    let raw = newPercentValue.toString().trim();

    // Normalize comma to dot
    raw = raw.replace(',', '.');

    // 👉 If user typed "1." or "1," don't calculate yet
    if (/^\d+\.$/.test(raw)) {
      return; // Do nothing until user types more digits
    }

    // Safe parse
    const percent = parseFloat(raw);
    if (isNaN(percent)) {
      return; // Ignore invalid intermediate input
    }

    // Calculate amount maintaining original sign
    const newAmount = Math.abs(totalAmount) * percent / 100;
    const newAmountWithSign = totalAmount < 0 ? -newAmount : newAmount;

    level.amount = parseFloat(newAmountWithSign.toFixed(2));

    this.amount_changed(level, level.amount);
  }

  getMissingAmount() {
    // Calculate the amount still missing to complete the total amount of the line
    const levelTotal = this.getLevelTotal();
    if (!this.plan || !this.plan.total_amount) {
      return 0;
    }
    const missing = this.plan.total_amount - levelTotal;
    return missing;
  }

  getMissingPercentage() {
    // Calculate the percentage still missing to complete 100%
    const currentPercentage = this.getTotalPercentage();
    const missing = 100 - currentPercentage;
    return missing;
  }

  getLevelTotal() {
    if (!this.screen_data.rows || this.screen_data.rows.length === 0) {
      return 0;
    }

    const seenPaths = new Set();
    let total = 0;

    for (const row of this.screen_data.rows) {
      // search for the deepest leaf node in the entire row
      let leafNode = null;
      let leafLevel = -1;

      // Iterate through the entire row to find the deepest leaf node
      for (let i = 0; i < row.path.length; i++) {
        const level = row.path[i];
        if (level && level.code) {
          const hasChildren = level.levels && level.levels.length > 0;
          if (!hasChildren) {
            leafNode = level;
            leafLevel = i;
          }
        }
      }

      // If we found a leaf node, check if it's unique and add it to the total
      if (leafNode) {
        const pathKey = row.path
          .slice(0, leafLevel + 1)
          .map(l => l ? l.code : 'null')
          .join('|');

        if (!seenPaths.has(pathKey)) {
          seenPaths.add(pathKey);
          const amount = parseFloat(leafNode.amount) || 0;
          total += amount;
        }
      }
    }

    return total;
  }

  getTotalPercentage() {
    // Calculate total percentage by dividing the sum of leaf amounts by plan total_amount
    const levelTotal = this.getLevelTotal();
    if (!this.plan || !this.plan.total_amount || this.plan.total_amount === 0) {
      return 0;
    }
    const percent = (Math.abs(levelTotal) / Math.abs(this.plan.total_amount)) * 100;
    return Math.round(percent * 100) / 100; // Round to 2 decimals
  }
}
